import { Link } from "@tanstack/react-router";
import {
  BarChart2,
  DollarSign,
  CalendarX,
  Compass,
  ChevronLeft,
  ChevronRight,
  PawPrint,
  Shield,
  LogOut,
  type LucideIcon,
} from "lucide-react";

const TAB_ICONS: Record<string, LucideIcon> = {
  utilization: BarChart2,
  revenue: DollarSign,
  cancellation: CalendarX,
  guide: Compass,
};

interface NavRailProps {
  tabs: readonly { key: string; label: string }[];
  activeTab: string;
  onTabChange: (key: string) => void;
  collapsed: boolean;
  onToggleCollapse: () => void;
  userEmail?: string;
  isAdmin: boolean;
  onSignOut: () => void;
  lastUpdated?: string;
}

export function NavRail({
  tabs,
  activeTab,
  onTabChange,
  collapsed,
  onToggleCollapse,
  userEmail,
  isAdmin,
  onSignOut,
  lastUpdated,
}: NavRailProps) {
  return (
    <aside
      className="sticky top-0 h-screen shrink-0 flex flex-col border-r border-black/5 bg-white transition-[width] duration-200 ease-out font-brand"
      style={{ width: collapsed ? 64 : 220 }}
    >
      {/* Brand anchor */}
      <div className="flex items-center gap-2.5 h-16 px-4 overflow-hidden whitespace-nowrap border-b border-black/5">
        <div
          className="grid place-items-center rounded-md text-white shrink-0"
          style={{ width: 28, height: 28, background: "var(--brand-dark-teal)" }}
        >
          <PawPrint className="h-4 w-4" />
        </div>
        {!collapsed && (
          <div className="flex flex-col min-w-0 leading-tight">
            <span className="text-[15px] font-semibold tracking-tight text-gray-800">
              Fetch Time 2.0
            </span>
            {lastUpdated && (
              <span className="text-[10px] text-gray-400">Updated {lastUpdated}</span>
            )}
          </div>
        )}
      </div>

      {/* Nav items */}
      <nav className="flex flex-col gap-0.5 px-2.5 py-3 flex-1">
        {tabs.map((tab) => {
          const Icon = TAB_ICONS[tab.key] ?? BarChart2;
          const active = activeTab === tab.key;
          return (
            <button
              key={tab.key}
              onClick={() => onTabChange(tab.key)}
              title={tab.label}
              className={[
                "flex items-center gap-3 rounded-lg py-2.5 text-[13px] font-medium transition-colors overflow-hidden whitespace-nowrap",
                collapsed ? "justify-center px-0" : "px-3",
                active
                  ? "bg-[color-mix(in_srgb,var(--brand-dark-teal)_10%,transparent)] text-[var(--brand-dark-teal)]"
                  : "text-gray-500 hover:bg-gray-100 hover:text-gray-700",
              ].join(" ")}
            >
              <Icon className="h-[18px] w-[18px] shrink-0" />
              {!collapsed && <span>{tab.label}</span>}
            </button>
          );
        })}
      </nav>

      {/* User + collapse control */}
      <div className="flex flex-col gap-1 border-t border-black/5 px-2.5 py-3">
        {userEmail &&
          (collapsed ? (
            <div
              className="grid place-items-center mx-auto rounded-full bg-gray-100 text-gray-600 text-[12px] font-semibold uppercase"
              style={{ width: 28, height: 28 }}
              title={userEmail}
            >
              {userEmail.charAt(0)}
            </div>
          ) : (
            <span className="truncate text-[12px] text-gray-500 px-2 py-1 rounded-md bg-gray-100/70">
              {userEmail}
            </span>
          ))}

        {isAdmin && (
          <Link
            to="/admin/users"
            title="Admin"
            className={[
              "flex items-center gap-3 rounded-lg py-2 text-[13px] font-medium text-gray-500 hover:bg-gray-100 hover:text-gray-700 transition-colors overflow-hidden whitespace-nowrap",
              collapsed ? "justify-center px-0" : "px-3",
            ].join(" ")}
          >
            <Shield className="h-[18px] w-[18px] shrink-0" />
            {!collapsed && <span>Admin</span>}
          </Link>
        )}

        <button
          onClick={onSignOut}
          title="Sign Out"
          className={[
            "flex items-center gap-3 rounded-lg py-2 text-[13px] font-medium text-gray-500 hover:bg-gray-100 hover:text-gray-700 transition-colors overflow-hidden whitespace-nowrap",
            collapsed ? "justify-center px-0" : "px-3",
          ].join(" ")}
        >
          <LogOut className="h-[18px] w-[18px] shrink-0" />
          {!collapsed && <span>Sign Out</span>}
        </button>

        <button
          onClick={onToggleCollapse}
          title={collapsed ? "Expand menu" : "Collapse menu"}
          className={[
            "flex items-center gap-3 rounded-lg py-2 mt-0.5 text-[13px] font-medium text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-colors overflow-hidden whitespace-nowrap",
            collapsed ? "justify-center px-0" : "px-3",
          ].join(" ")}
        >
          {collapsed ? (
            <ChevronRight className="h-[18px] w-[18px] shrink-0" />
          ) : (
            <ChevronLeft className="h-[18px] w-[18px] shrink-0" />
          )}
          {!collapsed && <span>Collapse</span>}
        </button>
      </div>
    </aside>
  );
}
